import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Spinner, Badge } from "reactstrap";
import { Link, useParams } from "react-router-dom";
import { getPublicLandlordProfile } from "../../helpers/firestoreService";
import porcherWordmark from "../../assets/images/porcher-wordmark.png";

/**
 * Public, no-login-required profile page for a single Porcher landlord.
 * Linked from Stripe Connect onboarding as the connected account's
 * "business website" — so it needs to be real, reachable, and specific to
 * that landlord (not Porcher's own marketing site).
 */
const LandlordProfilePage = () => {
  const { uid } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const p = await getPublicLandlordProfile(uid);
        if (cancelled) return;
        if (!p) setNotFound(true);
        else setProfile(p);
      } catch (e) {
        console.error("Load landlord profile error:", e);
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  document.title = profile?.displayName
    ? `${profile.displayName} | Porcher`
    : "Landlord Profile | Porcher";

  return (
    <div className="min-vh-100 d-flex flex-column bg-light">
      <div className="py-3 text-center border-bottom bg-white">
        <Link to="/">
          <img src={porcherWordmark} alt="Porcher" style={{ height: 28 }} />
        </Link>
      </div>

      <Container className="flex-grow-1 py-5" style={{ maxWidth: 640 }}>
        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : notFound ? (
          <Card className="shadow-sm border-0">
            <CardBody className="text-center py-5">
              <i className="bx bx-user-x text-muted" style={{ fontSize: "2.5rem" }} />
              <h5 className="mt-2">Profile not found</h5>
              <p className="text-muted mb-0">This landlord doesn&apos;t have a public profile yet.</p>
            </CardBody>
          </Card>
        ) : (
          <Card className="shadow-sm border-0">
            <CardBody className="p-4 p-md-5">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 56, height: 56, background: "#556ee618", color: "#556ee6" }}
                >
                  <i className="bx bxs-building-house fs-3" />
                </div>
                <div>
                  <h4 className="mb-0">{profile.companyName || profile.displayName || "Independent Landlord"}</h4>
                  {profile.companyName && profile.displayName && (
                    <div className="text-muted small">{profile.displayName}</div>
                  )}
                </div>
              </div>

              {profile.propertyCount && (
                <Badge color="light" className="text-dark border mb-3 fw-normal">
                  <i className="bx bx-building me-1" />
                  Manages {profile.propertyCount} rental {Number(profile.propertyCount) === 1 ? "property" : "properties"}
                </Badge>
              )}

              <h6 className="text-uppercase text-muted small mb-2">About</h6>
              <p className="mb-4">
                {profile.aboutYou || "This landlord manages residential rental properties through Porcher."}
              </p>

              <hr />

              <p className="text-muted small mb-0">
                Rent for this landlord&apos;s properties is collected securely through{" "}
                <Link to="/">Porcher</Link>, a residential property management platform.
                Payments are processed by Stripe.
              </p>
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  );
};

export default LandlordProfilePage;
